import { ReactElement, useEffect, useRef } from 'react';
import { Route } from 'config';
import { Component as PlantIcon } from 'icons/icon-circle--plant.svg';

import useUpcomingMenus from 'hooks/useUpcomingMenus';
import useCurrentUser from 'hooks/useCurrentUser';
import { useUpcomingMenu } from 'contexts/UpcomingMenu';
import UpcomingMenuModel from 'models/UpcomingMenu';

import SkuGallery from 'components/shared/SkuGallery';
import Header from './Header';
import Details from './Details';
import PlanName from './PlanName';
import ChangeMealsButton from './ChangeMealsButton';
import { Programs } from '../../../models/Plan';

type PlanNameProps = {
  item: UpcomingMenuModel;
  showTutorialTooltip: boolean;
};

function MobilePlanName({ item, showTutorialTooltip }: PlanNameProps) {
  const {
    isModifiable,
    cart_id: cartId,
    menu_id: menuId,
  } = item;

  const cartUrl = Route.CARTS
    .replace(':menuId', menuId?.toString())
    .replace(':cartId', cartId?.toString());

  return (
    <div className="c-order__plan d-flex d-lg-none">
      <PlanName item={item}>
        {isModifiable && (
          <ChangeMealsButton
            href={cartUrl}
            showTutorialTooltip={showTutorialTooltip}
          />
        )}
      </PlanName>
    </div>
  );
}

type MenuProps = {
  showTutorialTooltip: boolean;
  showFavoritesAnnouncement: boolean;
  showAutoSkipTooltip: boolean;
  setIsLtoMenuSkipped: (isLtoMenuSkipped: boolean) => void;
};

function UpcomingMenu({
  showTutorialTooltip,
  showFavoritesAnnouncement,
  showAutoSkipTooltip,
  setIsLtoMenuSkipped,
}: MenuProps): ReactElement {
  const { upcomingMenus } = useUpcomingMenus();
  const { upcomingMenu, dispatch } = useUpcomingMenu();
  const { currentUser } = useCurrentUser();
  const { hasFavoritesEnabled } = currentUser;

  const upcomingMenuElem = useRef<HTMLDivElement>();

  const {
    isEnabled,
    isModifiable,
    isGalleryExpanded,
    menu_id: menuId,
    cart_id: cartId,
    cart_n_of_plan: cartNOfPlan,
    cart_n_of_plan_desc: cartNOfPlanDesc,
    upcoming_menu_items: upcomingMenuItems,
    coupon_amount: couponAmount,
    plan_name: planName,
    is_thanksgiving_menu: isThanksgivingMenu,
    fulfillment_status: fulfillmentStatus,
  } = upcomingMenu;

  const showDiscountBanner = !!couponAmount;

  useEffect(() => {
    dispatch({
      type: 'reload',
      // @ts-expect-error TODO: fix null safety
      data: upcomingMenus.find((menu) => menu.menu_id === menuId),
    });
  }, [upcomingMenus, dispatch, menuId]);

  useEffect(() => {
    if (isThanksgivingMenu) {
      setIsLtoMenuSkipped(fulfillmentStatus === 'skipped');
    }
  }, [isThanksgivingMenu, fulfillmentStatus, setIsLtoMenuSkipped]);

  // Used to determine whether to add a "Week X of.."
  // program banner to top of an upcoming menu component.
  function displayProgramCartHeading() {
    return planName.toLowerCase() === Programs.JUMPSTART_PROGRAM
        && cartNOfPlan != null && cartNOfPlan < 5;
  }

  return (
    <div
        // @ts-expect-error TODO: fix null safety
      ref={upcomingMenuElem}
      className={[
        'c-order',
        isEnabled ? '' : 'is-disabled',
      ].join(' ')}
    >
      {displayProgramCartHeading()
          && (
            <div className="c-upcoming-menu-cart-n-of-plan">
              <PlantIcon />
              {cartNOfPlanDesc}
            </div>
          )}
      <div
        className="c-order__content"
          // @ts-expect-error TODO: fix null safety
        style={displayProgramCartHeading() ? { borderTopLeftRadius: '0', borderTopRightRadius: '0', borderTop: '0' } : null}
      >
        {showDiscountBanner && (
        <div className="c-order__discount-banner">
          Your
          {' '}
          {couponAmount}
          {' '}
          coupon is applied!
        </div>
        )}
        <Header
          item={upcomingMenu}
          showTutorialTooltip={showTutorialTooltip}
          showAutoSkipTooltip={showAutoSkipTooltip}
        />

        <MobilePlanName
          item={upcomingMenu}
          showTutorialTooltip={showTutorialTooltip}
        />

        <Details item={upcomingMenu} />

        <SkuGallery
          items={upcomingMenuItems}
            // @ts-expect-error TODO: fix null safety
          cartId={isModifiable && isEnabled ? cartId : null}
          showFavoritesWidget={hasFavoritesEnabled}
          expanded={isGalleryExpanded}
          showFavoritesAnnouncement={showFavoritesAnnouncement}
          openProductsInNewTab={false}
        />
      </div>
    </div>
  );
}

export default UpcomingMenu;
